import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, CalendarX, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { RedeemBox } from "@/pages/Gift";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");
const btn = "inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm transition-colors disabled:opacity-50";

const NoSub = ({ b }) => (
  <div className="rounded-3xl bg-white border border-[#E6DEC3] p-6" data-testid="billing-no-sub">
    {b.gift_until ? <p className="text-[#2C4A3E]" data-testid="billing-gift">You're on a gift plan: <b>{b.plan_name}</b>, active until <b>{day(b.gift_until)}</b>. It won't renew or charge you.</p> :
    <p className="text-[#2C4A3E]">{b.plan === "demo" ? "Your account has free demo access, so there's nothing to pay or cancel." : b.plan_status === "active" ? "Your plan is provided by your school or family account." : "You don't have a paid plan yet."}</p>}
    {!b.gift_until && b.plan !== "demo" && b.plan_status !== "active" && <Link to="/pricing" data-testid="billing-see-plans" className={`${btn} mt-4 bg-[#C87D53] text-white`}>See plans</Link>}
  </div>
);

export default function Billing() {
  const [b, setB] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => http.get("/billing").then((r) => setB(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const act = async (path, msg) => {
    setBusy(true);
    try { await http.post(`/billing/${path}`); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try { window.location.href = (await http.post("/billing/portal", { origin_url: window.location.origin })).data.url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="billing-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Billing</span>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2 mb-8">My plan</h1>
      {b && !b.has_subscription && <NoSub b={b} />}
      {b?.has_subscription && (
        <div className="rounded-3xl bg-white border border-[#E6DEC3] p-6 space-y-5">
          <div>
            <p className="font-display text-2xl text-[#2C4A3E]" data-testid="billing-plan-name">{b.plan_name}</p>
            <p className="text-sm text-stone-500 mt-1">{b.learners > 1 ? `${b.learners} learners · ` : ""}{b.amount != null ? `$${b.amount.toFixed(2)} per ${b.interval || "period"}` : ""}</p>
          </div>
          {b.cancel_at_period_end
            ? <p className="rounded-2xl bg-[#FBF3E2] text-[#7A4A12] text-sm p-4" data-testid="billing-cancelling">Your plan is cancelled and won't renew. You keep full access until <b>{day(b.period_end)}</b>.</p>
            : <p className="text-sm text-stone-600" data-testid="billing-renews">{b.status === "active" || b.status === "trialing" ? <>Renews on <b>{day(b.period_end)}</b>.</> : `Status: ${b.status}`}</p>}
          <div className="flex flex-wrap gap-3">
            <button onClick={portal} disabled={busy} data-testid="billing-portal-btn" className={`${btn} bg-[#2C4A3E] text-white hover:bg-[#1E352B]`}><CreditCard className="w-4 h-4" />Update card & see invoices</button>
            {b.cancel_at_period_end
              ? <button onClick={() => act("resume", "Your plan will keep renewing")} disabled={busy} data-testid="billing-resume-btn" className={`${btn} bg-[#C87D53] text-white`}><RotateCcw className="w-4 h-4" />Keep my plan</button>
              : <button onClick={() => setAsk(true)} disabled={busy} data-testid="billing-cancel-btn" className={`${btn} border border-[#E6DEC3] text-[#8A3B2E] hover:bg-[#FBE9E7]`}><CalendarX className="w-4 h-4" />Cancel subscription</button>}
          </div>
        </div>
      )}
      {b && !b.has_subscription && (
        <div className="mt-6 rounded-3xl bg-white border border-[#E6DEC3] p-6" data-testid="billing-redeem">
          <p className="text-sm font-medium text-[#2C4A3E] mb-3">Redeem a gift code</p><RedeemBox onDone={load} />
        </div>
      )}
      <p className="text-xs text-stone-500 mt-6">Want to give ConsciousLearn to another family? <Link to="/gift" className="underline" data-testid="billing-gift-link">Send a gift</Link>. See our <Link to="/refunds" className="underline" data-testid="billing-refund-link">Refund & Cancellation Policy</Link>.</p>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent className="bg-[#FDFBF7]" data-testid="billing-cancel-dialog">
          <AlertDialogTitle className="font-display text-2xl text-[#2C4A3E]">Cancel your plan?</AlertDialogTitle>
          <AlertDialogDescription>You won't be charged again. You and your kids keep full access until {day(b?.period_end)}. You can resume any time before then.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-cancel-keep">Keep my plan</AlertDialogCancel>
            <AlertDialogAction onClick={() => act("cancel", "Your plan is cancelled. Access continues until the end of the period.")} data-testid="billing-cancel-confirm" className="bg-[#8A3B2E] hover:bg-[#6F2E24]">Yes, cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
